<template>
  <div :data-test-id="$testId('idmStatusContainer')">
    <header :class="$style.header">
      Identity Management
    </header>

    <ul :class="$style.list">
      <li
        :class="$style.statusItem"
        :data-test-id="$testId('identityManagementStatus')"
      >
        <Status v-bind="identityManagementStatusProps" />
      </li>
    </ul>
    <IconButton
      v-if="isScheduleImportsResultsVisible"
      :class="$style.scheduleImportsResultsBtn"
      :data-test-id="$testId('scheduleImportsResultsBtn')"
      hasNoBackground
      hasOutline
      icon="calendar"
      text="Scheduled Import Results"
      type="informational"
      @click="onScheduleImportResultsClick"
    />
    <span
      v-if="isScimActive"
      :class="$style.deactivateIntegrationSpan"
      :data-test-id="$testId('deactivateIdmdeactivateIdmSpan')"
      @click.prevent="handleDeactivate"
    >
      Deactivate IdM connection
    </span>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { IconButton } from '@jumpcloud/ui-components';
import { StatusColorNames } from 'client/util/Constants';
import { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import { mapActions, mapState } from 'vuex';
import { scimConnectionTypes } from 'client/util/Constants/ApplicationConstants';
import EventTypes from 'client/Insights/ActivityLog/constants/EventTypes';
import IdentityManagementMixin from 'client/applications/mixins/IdentityManagementMixin';
import Status from 'components/Status/Status.vue';
import Util from 'client/util/Util';

const { ItemTypeConsts, StoreModelMap, StoreModuleMap } = Constants;
const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];
const { application } = ItemTypeConsts;
const ApplicationModel = StoreModelMap[application];
const { SCIM } = scimConnectionTypes;

export default {
  name: 'ApplicationSCIMStatus',

  components: {
    IconButton,
    Status,
  },

  mixins: [
    IdentityManagementMixin,
  ],

  computed: {
    ...mapState(ApplicationModel, ['item', 'scimConnectionType']),

    identityManagementStatusProps() {
      return {
        label: 'Integration Status',
        color: this.isScimActive ? StatusColorNames.active : StatusColorNames.inactive,
      };
    },

    isCustomApiScheduledImportsEnabled() {
      return this.$hasFeatureFlag(this.$featureFlags.customApiScheduledImports);
    },

    isScheduleImportsResultsVisible() {
      // Scheduled import currently are only supported by custom non-scim applications.
      return this.isCustomApiScheduledImportsEnabled
      && !this.isScimApplication
      && this.isScimActive;
    },

    isScimApplication() {
      // Check if is prebuilt SCIM
      const isScimPrebuilt = this.item?.provision?.type?.split(':')[0] === SCIM.toLowerCase();
      // Check if is custom SCIM
      const isScimCustom = this.scimConnectionType === SCIM;

      return isScimPrebuilt || isScimCustom;
    },
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'clearFilters',
      'setFilter',
      'setSelectedView',
    ]),

    onScheduleImportResultsClick() {
      // Clear any existing Insights Log filters
      this.clearFilters();

      // Set the "correlationType" filter for the Insights Log
      this.setFilter({
        field: eventFields.correlationType,
        value: ['importuserjob'],
      });
      // Set the "initiatedBy" filter for the Insights Log
      this.setFilter({
        field: eventFields.initiatedBy,
        value: ['jumpcloud'],
      });

      // Set current quick view to 'User creates and deletes'
      const scheduledImportsView = {
        additionalColumns: [eventFields.resourceUsername],
        isQuickView: true,
        filters: [
          {
            field: eventFields.eventType,
            value: [
              EventTypes.userCreate,
              EventTypes.userDelete,
              EventTypes.userImportCompleted,
              EventTypes.userImportStarted,
              EventTypes.userImportError,
              EventTypes.userUpdate,
            ],
          },
        ],
        name: 'Scheduled Import Results',
      };
      this.setSelectedView(scheduledImportsView);

      Util.redirect('#/insights');
    },
  },
};
</script>

<style module>
  @value (jcError) from '~css/brand-colors.css';

  .deactivateIntegrationSpan {
    color: jcError;
    cursor: pointer;
    display: block;
    font-weight: 400;
    text-decoration: underline;
  }

  .header {
    font-size: 16px;
    font-weight: 800;
  }

  .list {
    display: inline-block;
    padding-left: 23px;
    text-align: left;
  }

  .scheduleImportsResultsBtn {
    margin-bottom: 13px;
  }

  .statusItem {
    list-style-type: none;
  }
</style>
